/**
 * Keyboard shortcuts, as listed in the config's `keybinds` section (see `config/default.kdl`).
 *
 * A key press is turned into the same canonical text as the core writes (`Ctrl+Shift+Z`,
 * `Delete`, `L`), then looked up, and the bind's action runs as a named command (see
 * `shared/commands.ts`). A single letter pressed with Shift also matches the letter alone,
 * unless `Shift+<letter>` is bound. While typing in a field, shortcuts are off except Escape
 * (which leaves the field).
 */
import type { Keybind } from "@protocol/Keybind";
import type { Commands } from "../shared/commands";

/** The shortcuts used before the config arrives (same as `config/default.kdl`). */
export const DEFAULT_KEYBINDS: Keybind[] = [
  { keys: "Ctrl+Z", action: "graph.undo", argument: null },
  { keys: "Ctrl+Shift+Z", action: "graph.redo", argument: null },
  { keys: "Ctrl+Y", action: "graph.redo", argument: null },
  { keys: "Ctrl+S", action: "project.save", argument: null },
  { keys: "Delete", action: "selection.delete", argument: null },
  { keys: "Backspace", action: "selection.delete", argument: null },
  { keys: "Escape", action: "selection.clear", argument: null },
  { keys: "L", action: "tool.link", argument: null },
  { keys: "F", action: "view.fit", argument: null },
  { keys: "3", action: "view.3d", argument: null },
  { keys: "D", action: "view.diagram", argument: null },
  { keys: "/", action: "search.focus", argument: null },
  { keys: "Ctrl+Space", action: "presets.drawer", argument: null },
  { keys: "P", action: "presets.drawer", argument: null },
  { keys: "N", action: "node.add", argument: null },
  { keys: "C", action: "group.fold", argument: null },
  { keys: "F2", action: "group.rename", argument: null },
  { keys: "Ctrl+K", action: "palette.open", argument: null },
  { keys: "Ctrl+D", action: "board.duplicate", argument: null },
  { keys: "Ctrl+G", action: "board.group", argument: null },
  { keys: "Ctrl+Shift+G", action: "board.ungroup", argument: null },
  { keys: "Ctrl+]", action: "board.front", argument: null },
  { keys: "Ctrl+[", action: "board.back", argument: null },
  { keys: "V", action: "board.tool", argument: "select" },
  { keys: "H", action: "board.tool", argument: "hand" },
  { keys: "S", action: "board.tool", argument: "note" },
  { keys: "T", action: "board.tool", argument: "text" },
  { keys: "R", action: "board.tool", argument: "shape" },
  { keys: "A", action: "board.tool", argument: "arrow" },
  { keys: "Shift+F", action: "board.tool", argument: "frame" },
  { keys: "I", action: "board.tool", argument: "image" },
  { keys: "W", action: "board.tool", argument: "pen" },
  { keys: "E", action: "board.tool", argument: "eraser" },
  { keys: "B", action: "board.tool", argument: "button" },
  { keys: "Shift+I", action: "board.tool", argument: "input" },
  { keys: "X", action: "board.tool", argument: "checkbox" },
  { keys: "Shift+L", action: "board.tool", argument: "list" },
  { keys: "Shift+E", action: "board.prototype", argument: null },
  { keys: "Shift+P", action: "prototype.play", argument: null },
];

/** The keys bound to `command` (with `argument`) in `binds`, as written in the config (`Ctrl+Z`), or `""`. */
export function shortcutOf(binds: Keybind[], command: string | undefined, argument: string | null = null): string {
  if (!command) return "";
  return binds.find((b) => b.action === command && (b.argument ?? null) === argument)?.keys ?? "";
}

/** The parts of a key event a shortcut depends on. */
type KeyPress = Pick<KeyboardEvent, "key" | "ctrlKey" | "altKey" | "shiftKey" | "metaKey">;

/** What a key event looks like as text, e.g. `Ctrl+Shift+Z`. */
export function comboOf(e: KeyPress): string {
  let key = e.key === " " ? "Space" : e.key.length === 1 ? e.key.toUpperCase() : e.key;
  if (key === "Del") key = "Delete";
  if (key === "Esc") key = "Escape";
  const parts = [e.ctrlKey && "Ctrl", e.altKey && "Alt", e.shiftKey && "Shift", e.metaKey && "Meta"].filter(Boolean) as string[];
  // Shift changes the character itself for symbols ("?" is Shift+/), so it is not added again.
  if (e.shiftKey && key.length === 1 && !/[A-Z]/.test(key)) parts.splice(parts.indexOf("Shift"), 1);
  return [...parts, key].join("+");
}

/** The binding for a key event, if any. */
export function findBinding(binds: Keybind[], e: KeyPress): Keybind | undefined {
  const combo = comboOf(e);
  const exact = binds.find((b) => b.keys === combo);
  if (exact) return exact;
  if (e.shiftKey && /^[A-Z]$/.test(combo.slice(-1)) && combo.endsWith(`Shift+${combo.slice(-1)}`)) {
    return binds.find((b) => b.keys === combo.replace("Shift+", ""));
  }
  return undefined;
}

/** `true` for a key press that opens the developer tools or the page source. */
export function opensDevTools(e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey">): boolean {
  const key = e.key.toUpperCase();
  const ctrl = e.ctrlKey || e.metaKey;
  return key === "F12" || (ctrl && e.shiftKey && ["I", "J", "C"].includes(key)) || (ctrl && e.altKey && ["I", "J", "C"].includes(key)) || (ctrl && !e.shiftKey && key === "U");
}

/** `true` while the keyboard focus is in a text field (shortcuts must not steal letters). */
function isTyping(element: Element | null): element is HTMLElement {
  return element instanceof HTMLElement && (/^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName) || element.isContentEditable);
}

/**
 * Installs the shortcuts. `binds` gives the current list (it follows the config). Unknown
 * actions do nothing (the core already warned about them). Returns a function that removes
 * the shortcuts.
 */
export function installShortcuts(commands: Commands, binds: () => Keybind[]): () => void {
  /** Looks the key up and runs its command. */
  const onKey = (e: KeyboardEvent) => {
    if (e.defaultPrevented) return;
    const active = document.activeElement;
    if (isTyping(active)) {
      if (e.key === "Escape") active.blur();
      return;
    }
    const bind = findBinding(binds(), e);
    if (!bind) return;
    e.preventDefault();
    commands.run(bind.action, bind.argument);
  };
  addEventListener("keydown", onKey);
  return () => removeEventListener("keydown", onKey);
}
